import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import posthog from 'posthog-js';
import { resetTTSVoices } from '../lib/tts';
import { getAttribution } from '../lib/attribution';
import { setSpeechaceBands } from '../lib/score';
import { setSoundLessons } from '../lib/soundLessons';
import { setRecDiag } from '../lib/speech';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    // Pronunciation changes being tried on this account first (the server's
    // pronunciation_beta): Speechace's own colour bands and wording. Set during
    // render, not in an effect, so the first result drawn already uses them.
    setSpeechaceBands(!!user?.pronunciation_beta);
    setSoundLessons(!!user?.sound_lessons);
    setRecDiag(!!user?.rec_diag);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) { setLoading(false); return; }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        let cancelled = false;
        axios.get(`${API}/auth/me`)
            .then(res => {
                if (!cancelled) {
                    setUser(res.data);
                    posthog.identify(res.data.id, { email: res.data.email, name: res.data.name });
                    posthog.capture('session_started');
                }
            })
            .catch(err => { console.error('Auth error:', err); if (!cancelled) logout(); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async (email, password) => {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = res.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        resetTTSVoices(); // voices are per plan, so never carry them across users
        posthog.identify(userData.id, { email: userData.email, name: userData.name });
        posthog.capture('session_started');
        return userData;
    };

    const register = async (email, password, name) => {
        // Where they came from, recorded on their first ever page view. Sent
        // only here: it describes how the account began, so it is written once
        // at sign-up and never touched again.
        const res = await axios.post(`${API}/auth/register`, {
            email, password, name, source: getAttribution(),
        });
        const { token: newToken, user: userData } = res.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        resetTTSVoices();
        posthog.identify(userData.id, { email: userData.email, name: userData.name });
        posthog.capture('signup_completed');
        sessionStorage.setItem('newUser', '1');
        return userData;
    };

    // Re-fetch /auth/me so consumers see fresh user fields (e.g. after onboarding
    // completes and sets onboarding_complete=true on the server).
    const refreshUser = async () => {
        if (!token) return null;
        try {
            const res = await axios.get(`${API}/auth/me`);
            setUser(res.data);
            return res.data;
        } catch (err) {
            console.error('refreshUser failed:', err);
            return null;
        }
    };

    const logout = () => {
        // Clear session-only highlights for this user
        if (user?.id) sessionStorage.removeItem(`new_phrase_ids_${user.id}`);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        resetTTSVoices();
        posthog.reset();
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
